import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { CalendarIcon, ClockIcon, UserIcon, LockIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  author_id: string;
  status: string;
  published_at: string;
  featured_image_url: string;
  category: string;
  tags: string[];
  blog_types: string[];
  created_at: string;
  updated_at: string;
  staff_members?: {
    first_name: string;
    last_name: string;
  } | null;
}

const Insights = () => {
  const [insightsPosts, setInsightsPosts] = useState<BlogPost[]>([]);
  const [featuredPost, setFeaturedPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    // Check authentication status
    const checkAuth = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setIsAuthenticated(!!user);
    };
    
    checkAuth();
  }, []);

  useEffect(() => {
    const fetchInsightsPosts = async () => {
      console.log('Fetching insights posts...');
      try {
        // Fetch posts that include 'insights' in blog_types
        const { data: posts, error: postsError } = await supabase
          .from('blog_posts')
          .select('*')
          .eq('status', 'published')
          .contains('blog_types', ['insights'])
          .order('published_at', { ascending: false });

        console.log('Insights posts query result:', { posts, postsError });

        if (postsError) {
          console.error('Error fetching insights posts:', postsError);
          setLoading(false);
          return;
        }

        if (posts && posts.length > 0) {
          // Get unique author IDs
          const authorIds = [...new Set(posts.map(post => post.author_id))];
          
          // Fetch author information
          const authorPromises = authorIds.map(async (authorId) => {
            try {
              const { data: authorData, error } = await supabase.rpc('get_blog_author', {
                author_user_id: authorId
              });
              
              if (error) {
                console.error('Error fetching author:', error);
                return { userId: authorId, staffMember: null };
              }
              
              if (authorData && authorData.length > 0) {
                return {
                  userId: authorId,
                  staffMember: authorData[0]
                };
              }
              
              return { userId: authorId, staffMember: null };
            } catch (err) {
              console.error('Exception fetching author:', err);
              return { userId: authorId, staffMember: null };
            }
          });

          try {
            const authorResults = await Promise.all(authorPromises);

            // Create author lookup map
            const authorMap: Record<string, any> = {};
            authorResults.forEach(result => {
              if (result.staffMember) {
                authorMap[result.userId] = result.staffMember;
              }
            });

            // Attach author info to posts
            const postsWithAuthors = posts.map(post => ({
              ...post,
              staff_members: authorMap[post.author_id] || null
            }));

            setFeaturedPost(postsWithAuthors[0]);
            setInsightsPosts(postsWithAuthors.slice(1));
          } catch (authorError) {
            console.error('Error processing authors:', authorError);
            setFeaturedPost(posts[0]);
            setInsightsPosts(posts.slice(1));
          }
        }
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated) {
      fetchInsightsPosts();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  const estimateReadTime = (content: string) => {
    const wordsPerMinute = 200;
    const textContent = content.replace(/<[^>]*>/g, '');
    const wordCount = textContent.split(/\s+/).length;
    const readTime = Math.ceil(wordCount / wordsPerMinute);
    return `${readTime} min read`;
  };

  const getAuthorName = (post: BlogPost) => {
    if (post.staff_members) {
      return `${post.staff_members.first_name} ${post.staff_members.last_name}`;
    }
    return 'Unknown Author';
  };

  if (!isAuthenticated) {
    return (
      <div className="py-8">
        <section className="bg-gradient-to-br from-primary to-accent text-primary-foreground py-16">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <LockIcon className="h-16 w-16 mx-auto mb-4 opacity-80" />
              <h1 className="text-4xl md:text-5xl font-bold mb-6">Partner Insights</h1>
              <p className="text-xl text-primary-foreground/90 max-w-3xl mx-auto">
                Exclusive analysis and internal insights for team members
              </p>
            </div>
          </div>
        </section>

        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <h2 className="text-2xl font-bold mb-4">Authentication Required</h2>
            <p className="text-muted-foreground mb-6">
              Please sign in to access partner insights and internal analysis.
            </p>
            <Button asChild>
              <Link to="/partner/login">Sign In</Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="py-8">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center items-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading insights...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!featuredPost && insightsPosts.length === 0) {
    return (
      <div className="py-8">
        <section className="bg-gradient-to-br from-primary to-accent text-primary-foreground py-16">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-6">Partner Insights</h1>
              <p className="text-xl text-primary-foreground/90 max-w-3xl mx-auto">
                Exclusive analysis and internal insights for team members
              </p>
            </div>
          </div>
        </section>

        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">💡</span>
            </div>
            <h2 className="text-2xl font-bold mb-4">No Insights Available</h2>
            <p className="text-muted-foreground mb-6">
              There are no insights posts available at the moment. Check back soon!
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-8">
      {/* Header */}
      <section className="bg-gradient-to-br from-primary to-accent text-primary-foreground py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <Badge variant="secondary" className="mb-4">Partner Only</Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Partner Insights</h1>
            <p className="text-xl text-primary-foreground/90 max-w-3xl mx-auto">
              Exclusive analysis and internal insights for team members
            </p>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
        {/* Featured Insight */}
        {featuredPost && (
          <div className="mb-12">
            <h2 className="text-2xl font-bold mb-6">Featured Insight</h2>
            <Card className="overflow-hidden">
              <div className="aspect-video relative">
                {featuredPost.featured_image_url ? (
                  <img 
                    src={featuredPost.featured_image_url} 
                    alt={featuredPost.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="bg-gradient-to-br from-primary/10 to-accent/10 w-full h-full flex items-center justify-center">
                    <div className="text-center">
                      <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                        <span className="text-2xl">💡</span>
                      </div>
                    </div>
                  </div>
                )}
                <div className="absolute top-4 left-4">
                  <Badge>{featuredPost.category}</Badge>
                </div>
              </div>
              <CardHeader>
                <CardTitle className="text-2xl">{featuredPost.title}</CardTitle>
                <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <UserIcon className="h-4 w-4" />
                    {getAuthorName(featuredPost)}
                  </div>
                  <div className="flex items-center gap-1">
                    <CalendarIcon className="h-4 w-4" />
                    {formatDate(featuredPost.published_at)}
                  </div>
                  <div className="flex items-center gap-1">
                    <ClockIcon className="h-4 w-4" />
                    {estimateReadTime(featuredPost.content)}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base mb-6">
                  {featuredPost.excerpt}
                </CardDescription>
                <Button asChild>
                  <Link to={`/insights/${featuredPost.slug}`}>Read Full Insight</Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Recent Insights */}
        {insightsPosts.length > 0 && (
          <div>
            <h2 className="text-2xl font-bold mb-6">Recent Insights</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {insightsPosts.map((post) => (
                <Card key={post.id} className="overflow-hidden">
                  <div className="aspect-video relative">
                    {post.featured_image_url ? (
                      <img 
                        src={post.featured_image_url} 
                        alt={post.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="bg-gradient-to-br from-muted to-muted/50 w-full h-full flex items-center justify-center">
                        <span className="text-2xl">💡</span>
                      </div>
                    )}
                    <div className="absolute top-2 left-2">
                      <Badge>{post.category}</Badge>
                    </div>
                  </div>
                  <CardHeader>
                    <CardTitle className="text-lg line-clamp-2">{post.title}</CardTitle>
                    <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <UserIcon className="h-3 w-3" />
                        {getAuthorName(post)}
                      </div>
                      <div className="flex items-center gap-1">
                        <CalendarIcon className="h-3 w-3" />
                        {formatDate(post.published_at)}
                      </div>
                      <div className="flex items-center gap-1">
                        <ClockIcon className="h-3 w-3" />
                        {estimateReadTime(post.content)}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-sm line-clamp-3 mb-4">
                      {post.excerpt}
                    </CardDescription>
                    <Button variant="outline" size="sm" asChild>
                      <Link to={`/insights/${post.slug}`}>Read More</Link>
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Insights;
